/**
 * Acquisition Page - Funnel and lead source analytics
 *
 * Shows:
 * - Cash metrics (full amounts, not abbreviated)
 * - Lead funnel metrics (Applications, Opt-ins, Calls, Closes)
 * - Lead source breakdown chart
 * - Funnel breakdown chart (Applicant vs Opt-in)
 * - Deal status breakdown chart
 */

import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "../context/ThemeContext";
import { MetricCard, MetricCardSkeleton, StatCard, StatCardSkeleton, FilterBar } from "../components";
import { AdditionalStatCardSkeleton } from "../components/StatCard";
import { useDateRange } from "../context/DateRangeContext";
import { useFilters } from "../context/FilterContext";
import { useSession } from "../auth/client";
import { ResponsivePie } from "@nivo/pie";
import { branding } from "../config/branding";
import { DataTable, type ColumnDef } from "../components/ui/data-table";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import { EditRowDialog, type FieldDefinition } from "../components/EditRowDialog";

// Icons
const DollarIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrendingUpIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const ChartIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

// Formatters
const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

const formatCurrencyAbbreviated = (value: number) => {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return formatCurrency(value);
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

// Chart colors from branding
const PIE_COLORS = branding.colors.chart;

const CRIMSON_CHART_COLORS = [
  "#ef4444", "#dc2626", "#b91c1c", "#991b1b", "#7f1d1d",
  "#f87171", "#fca5a5", "#c53030", "#e53e3e", "#fc8181", "#be2626",
];

// Nivo pie theme
const getNivoPieTheme = () => ({
  labels: { text: { fontSize: 12, fontWeight: 600 } },
  legends: { text: { fontSize: 11 } },
});

// Custom tooltip component for Nivo pie charts (works in dark/light mode)
const PieChartTooltip = ({ datum }: { datum: { id: string | number; value: number; color: string; data: { percentage: number } } }) => (
  <div className="bg-card text-foreground border border-border rounded-lg shadow-xl px-3 py-2 text-sm">
    <div className="flex items-center gap-2">
      <div
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: datum.color }}
      />
      <span className="font-medium">{datum.id}</span>
    </div>
    <div className="mt-1 text-muted-foreground">
      {datum.value.toLocaleString()} ({datum.data.percentage.toFixed(1)}%)
    </div>
  </div>
);

// Transform data for Nivo pie chart
const toNivoPieData = (
  data: Array<{ name: string; value: number; percentage?: number }>,
  colors: string[]
) => {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const merged = data.map((item, index) => ({
    id: item.name,
    label: item.name,
    value: item.value,
    color: colors[index % colors.length],
    percentage: total > 0 ? (item.value / total) * 100 : 0,
  }));
  // Stable sort: by value descending, then by name alphabetically
  merged.sort((a, b) => {
    if (b.value !== a.value) return b.value - a.value;
    return a.label.localeCompare(b.label);
  });
  return merged;
};

interface DashboardMetrics {
  totalCashCollected: number;
  totalRevenue: number;
  closeRate: number;
  showRate: number;
  callsDue: number;
  callsTaken: number;
  callsClosed: number;
  avgCashPerCall: number;
  avgCashPerClose: number;
  avgCashPerApplication: number;
  avgCashPerOptin: number;
  totalApplicationsCash: number;
  totalOptinsCash: number;
  newCashCollected: number;
  mrrCash: number;
  totalGrossRevenue: number;
  totalApplications: number;
  totalOptins: number;
}

interface ChartDataItem {
  name: string;
  value: number;
  percentage?: number;
}

interface OutcomeItem {
  _id: string;
  count: number;
  cash: number;
}

interface FilterOption {
  value: string;
  label: string;
}

interface RecentCall {
  id: string;
  _id: string;
  timestamp: string;
  dateCallWasTaken: string;
  leadName: string;
  email: string;
  phone: string;
  closer: string;
  setter: string;
  status: "closed" | "no-show" | "pending" | "not-interested";
  callOutcome: string;
  cashCollected: number;
  notes: string;
}

// Funnel interfaces
interface FunnelLead {
  name: string;
  email: string;
  phone?: string;
  timestamp: string;
  dateCallWasTaken?: string;
  callOutcome?: string;
  cashCollected?: number;
}

interface FunnelStepData {
  label: string;
  count: number;
  leads: FunnelLead[];
}

interface FunnelApiData {
  optins: FunnelLead[];
  applications: FunnelLead[];
  bookedCalls: FunnelLead[];
  showedCalls: FunnelLead[];
  closedCalls: FunnelLead[];
}

// Crimson funnel step colors (light → dark as funnel narrows)
const FUNNEL_STEP_COLORS = [
  CRIMSON_CHART_COLORS[6],  // lightest — Opt-ins
  CRIMSON_CHART_COLORS[5],  // Applications
  CRIMSON_CHART_COLORS[0],  // Booked Calls
  CRIMSON_CHART_COLORS[1],  // Showed Calls
  CRIMSON_CHART_COLORS[2],  // darkest — Closed Calls
];


// Perfect inverted-triangle funnel — uniform bands, purely aesthetic shape
function FunnelChart({
  steps,
  selectedIndex,
  onStepClick,
}: {
  steps: FunnelStepData[];
  selectedIndex: number;
  onStepClick: (index: number) => void;
}) {
  const N = steps.length;
  const SVG_W = 500;
  const CENTER_X = SVG_W / 2;
  const MAX_HALF_W = 240; // widest point at top
  const TIP_HALF_W = 7;   // narrow tip at bottom
  const FUNNEL_H = 490;
  const STEP_H = FUNNEL_H / N;
  const TOP_PAD = 12;
  const SVG_H = TOP_PAD + FUNNEL_H + 12;

  // Linear half-width at fraction f (0=top, 1=bottom) of the triangle
  const hwAt = (f: number) => MAX_HALF_W + (TIP_HALF_W - MAX_HALF_W) * f;

  return (
    <svg
      viewBox={`0 0 ${SVG_W} ${SVG_H}`}
      className="w-full text-foreground"
      style={{ display: "block" }}
    >
      {steps.map((step, i) => {
        const y = TOP_PAD + i * STEP_H;
        const topHW = hwAt(i / N);
        const botHW = hwAt((i + 1) / N);
        const topL = CENTER_X - topHW;
        const topR = CENTER_X + topHW;
        const botL = CENTER_X - botHW;
        const botR = CENTER_X + botHW;
        const path = `M ${topL} ${y} L ${topR} ${y} L ${botR} ${y + STEP_H} L ${botL} ${y + STEP_H} Z`;
        const color = FUNNEL_STEP_COLORS[i];
        const midY = y + STEP_H / 2;
        const isSelected = i === selectedIndex;

        const prevCount = i > 0 ? steps[i - 1].count : null;
        const convRate =
          prevCount !== null && prevCount > 0
            ? ((step.count / prevCount) * 100).toFixed(1)
            : null;

        // Fix conversion rate x at a consistent left margin
        const convX = CENTER_X - MAX_HALF_W - 8;

        return (
          <g key={step.label} style={{ cursor: "pointer" }} onClick={() => onStepClick(i)}>
            <path
              d={path}
              fill={color}
              opacity={isSelected ? 1 : 0.52}
              stroke="none"
              style={{ transition: "opacity 0.15s" }}
              onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.opacity = "0.76"; }}
              onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.opacity = "0.52"; }}
            />
            {/* White divider line between sections (not on last) */}
            {i < N - 1 && (
              <line
                x1={botL} y1={y + STEP_H}
                x2={botR} y2={y + STEP_H}
                stroke="white" strokeOpacity={0.15} strokeWidth={1}
                style={{ pointerEvents: "none" }}
              />
            )}
            {/* Selected highlight overlay */}
            {isSelected && (
              <path
                d={path}
                fill="white"
                fillOpacity={0.08}
                stroke="white"
                strokeOpacity={0.4}
                strokeWidth={1.5}
                style={{ pointerEvents: "none" }}
              />
            )}
            <text
              x={CENTER_X} y={midY - 11}
              textAnchor="middle" dominantBaseline="middle"
              fill="white" fontSize={11} fontWeight="600"
              opacity={isSelected ? 1 : 0.8}
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              {step.label}
            </text>
            <text
              x={CENTER_X} y={midY + 11}
              textAnchor="middle" dominantBaseline="middle"
              fill="white" fontSize={18} fontWeight="800"
              style={{ pointerEvents: "none", userSelect: "none" }}
            >
              {step.count.toLocaleString()}
            </text>
            {convRate !== null && (
              <text
                x={convX} y={y}
                textAnchor="end" dominantBaseline="middle"
                fill="currentColor" fontSize={9} opacity={0.38}
                style={{ pointerEvents: "none", userSelect: "none" }}
              >
                ↓ {convRate}%
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

// Inline lead table (no modal) — searchable, with phone and optional cash column
function FunnelLeadTable({ step }: { step: FunnelStepData | null }) {
  const [search, setSearch] = useState("");

  // Reset search when step changes
  useEffect(() => { setSearch(""); }, [step?.label]);

  const showCash = step?.label === "Closed Calls";

  const filteredLeads = useMemo(() => {
    if (!step?.leads) return [];
    if (!search.trim()) return step.leads;
    const q = search.toLowerCase();
    return step.leads.filter(
      (l) =>
        (l.name || "").toLowerCase().includes(q) ||
        (l.email || "").toLowerCase().includes(q) ||
        (l.phone || "").replace(/\D/g, "").includes(q.replace(/\D/g, ""))
    );
  }, [step?.leads, search]);

  if (!step) return <div className="flex items-center justify-center h-full text-muted-foreground text-sm">Select a step</div>;

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Header */}
      <div className="mb-2 flex-shrink-0">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{step.label}</p>
        <p className="text-2xl font-bold mt-0.5">{step.count.toLocaleString()}</p>
      </div>
      {/* Search */}
      <div className="mb-3 flex-shrink-0 relative">
        <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, email, or phone..."
          className="w-full pl-8 pr-3 py-1.5 text-xs rounded-md bg-muted/40 border border-border focus:outline-none focus:border-foreground/30 transition-colors"
        />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      {filteredLeads.length === 0 ? (
        <div className="flex items-center justify-center flex-1 text-muted-foreground text-sm">
          {search ? "No leads match your search" : "No leads in this step"}
        </div>
      ) : (
        <div className="overflow-y-auto flex-1 pr-2">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card z-10">
              <tr className="border-b border-border">
                <th className="text-left font-semibold text-muted-foreground pb-2 pr-3 pt-1">Name</th>
                <th className="text-left font-semibold text-muted-foreground pb-2 pr-3 pt-1">Email</th>
                <th className="text-left font-semibold text-muted-foreground pb-2 pr-3 pt-1">Phone</th>
                <th className="text-left font-semibold text-muted-foreground pb-2 pt-1 pr-3">Date</th>
                {showCash && <th className="text-left font-semibold text-muted-foreground pb-2 pt-1 pr-4">Cash</th>}
              </tr>
            </thead>
            <tbody>
              {filteredLeads.map((lead, idx) => {
                const dateStr = lead.timestamp
                  ? new Date(lead.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
                  : lead.dateCallWasTaken || "-";
                return (
                  <tr key={idx} className="border-b border-border/30 hover:bg-muted/20 transition-colors">
                    <td className="py-2 pr-3 font-medium truncate max-w-[110px]">{lead.name || "Unknown"}</td>
                    <td className="py-2 pr-3 text-muted-foreground truncate max-w-[140px]">{lead.email || "-"}</td>
                    <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">{lead.phone || "-"}</td>
                    <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">{dateStr}</td>
                    {showCash && (
                      <td className="py-2 pr-4 font-semibold text-green-500">
                        {lead.cashCollected && lead.cashCollected > 0 ? formatCurrency(lead.cashCollected) : "-"}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Field definitions for editing calls
const CALL_EDIT_FIELDS: FieldDefinition[] = [
  { key: "leadName", label: "Lead Name", type: "text", required: true },
  { key: "email", label: "Email", type: "email" },
  { key: "phone", label: "Phone", type: "tel" },
  { key: "closer", label: "Closer", type: "text" },
  { key: "setter", label: "Setter", type: "text" },
  { key: "callOutcome", label: "Call Outcome", type: "text" },
  { key: "notes", label: "Notes", type: "textarea" },
];

export function Acquisition() {
  const { theme } = useTheme();
  const isCrimson = theme === "crimson";
  const chartColors = CRIMSON_CHART_COLORS;
  const { data: session } = useSession();
  const { dateRange: globalDateRange, formatForApi } = useDateRange();
  const {
    dateField,
    closer,
    closers,
    setter,
    setters,
    source,
    sources,
    content,
    country,
    funnel,
    funnels,
    leadType,
    leadTypes,
    setDateField,
    setClosers,
    setSetters,
    setSources,
    setContents,
    setCountries,
    setFunnels,
    setLeadTypes,
    clearAllFilters,
  } = useFilters();

  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState<DashboardMetrics | null>(null);
  const [cashTrend, setCashTrend] = useState<{ date: string; value: number }[]>([]);
  const [countryData, setCountryData] = useState<ChartDataItem[]>([]);
  const [leadSourceData, setLeadSourceData] = useState<ChartDataItem[]>([]);
  const [outcomeBreakdown, setOutcomeBreakdown] = useState<OutcomeItem[]>([]);
  const [prospectSituationData, setProspectSituationData] = useState<ChartDataItem[]>([]);
  const [filterOptions, setFilterOptions] = useState<{
    closers: string[];
    setters: string[];
    funnels: FilterOption[];
    contents: FilterOption[];
    countries: string[];
    sources: string[];
  }>({
    closers: [],
    setters: [],
    funnels: [],
    contents: [],
    countries: [],
    sources: [],
  });
  const [chartFilterMode, setChartFilterMode] = useState<"closed" | "all">("closed");

  // Recent Calls table state
  const [recentCalls, setRecentCalls] = useState<RecentCall[]>([]);
  const [tablePage, setTablePage] = useState(1);
  const [tablePageSize, setTablePageSize] = useState(10);
  const [tableTotalItems, setTableTotalItems] = useState(0);
  const [tableTotalPages, setTableTotalPages] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);

  // Funnel state
  const [funnelData, setFunnelData] = useState<FunnelApiData | null>(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [selectedFunnelIndex, setSelectedFunnelIndex] = useState(0);

  // Edit/Delete state for recent calls
  const [editingCall, setEditingCall] = useState<RecentCall | null>(null);
  const [deletingCall, setDeletingCall] = useState<RecentCall | null>(null);

  // Track if the next fetch should be table-only (for pagination changes)
  const isPaginationChangeRef = useRef(false);

  // Status badge helper
  const getStatusBadgeClass = (status: RecentCall["status"]) => {
    const styles: Record<string, string> = {
      closed: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
      "no-show": "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
      pending: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
      "not-interested": "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    };
    return styles[status] || styles.pending;
  };

  const getStatusLabel = (status: RecentCall["status"]) => {
    const labels: Record<string, string> = {
      closed: "Closed",
      "no-show": "No Show",
      pending: "Pending",
      "not-interested": "Not Interested",
    };
    return labels[status] || status;
  };

  // Define columns for recent calls table
  const recentCallsColumns: ColumnDef<RecentCall>[] = useMemo(() => [
    {
      id: "timestamp",
      header: "Timestamp",
      cell: (row) => {
        if (!row.timestamp) return <span className="text-muted-foreground">-</span>;
        const date = new Date(row.timestamp);
        return (
          <span className="text-muted-foreground text-xs">
            {date.toLocaleDateString()} {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        );
      },
    },
    {
      id: "dateCallWasTaken",
      header: "Date Call Was Taken",
      cell: (row) => (
        row.dateCallWasTaken ? (
          <span className="text-muted-foreground">{row.dateCallWasTaken}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      ),
    },
    {
      id: "leadName",
      header: "Lead Name",
      accessorKey: "leadName",
      className: "font-medium",
    },
    {
      id: "closer",
      header: "Closer",
      accessorKey: "closer",
    },
    {
      id: "setter",
      header: "Setter",
      accessorKey: "setter",
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => (
        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(row.status)}`}>
          {getStatusLabel(row.status)}
        </span>
      ),
    },
    {
      id: "cashCollected",
      header: "Cash Collected",
      cell: (row) => (
        row.cashCollected > 0 ? (
          <span className="text-green-600 font-medium">{formatCurrency(row.cashCollected)}</span>
        ) : (
          <span className="text-muted-foreground">-</span>
        )
      ),
      headerClassName: "text-right",
      className: "text-right",
    },
  ], []);

  // Handle edit
  const handleEditCall = (call: RecentCall) => {
    setEditingCall(call);
  };

  // Handle save
  const handleSaveCall = async (data: Record<string, unknown>) => {
    const mappedData = {
      prospectName: data.leadName,
      prospectEmail: data.email,
      prospectNumber: data.phone,
      closerName: data.closer,
      setterName: data.setter,
      callOutcome: data.callOutcome,
      callNotes: data.notes,
    };

    const response = await fetch(`/api/leads/${data._id || data.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(mappedData),
    });

    if (!response.ok) {
      throw new Error("Failed to update call");
    }

    // Refresh data
    fetchData(false);
  };

  // Handle delete
  const handleDeleteCall = (call: RecentCall) => {
    setDeletingCall(call);
  };

  // Confirm delete
  const confirmDeleteCall = async () => {
    if (!deletingCall) return;

    const response = await fetch(`/api/leads/${deletingCall._id || deletingCall.id}`, {
      method: "DELETE",
    });

    if (!response.ok) {
      throw new Error("Failed to delete call");
    }

    // Refresh data
    fetchData(false);
  };

  // Handle pagination changes
  const handlePageChange = (page: number) => {
    isPaginationChangeRef.current = true;
    setTablePage(page);
  };

  const handlePageSizeChange = (size: number) => {
    isPaginationChangeRef.current = true;
    setTablePageSize(size);
    setTablePage(1);
  };

  // Fetch data
  // tableOnly: if true, only show table loading (for pagination changes)
  const fetchData = useCallback(async (showLoading = true, tableOnly = false) => {
    if (showLoading) {
      if (tableOnly) {
        setTableLoading(true);
      } else {
        setIsLoading(true);
      }
    }
    try {
      const params = new URLSearchParams();

      // Add date range
      const { startDate, endDate } = formatForApi();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);

      // Add filters
      if (dateField) params.set("dateField", dateField);
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));
      if (sources.length > 0) params.set("source", sources.join(","));
      if (content) params.set("content", content);
      if (country) params.set("country", country);
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      if (leadTypes.length > 0) params.set("leadType", leadTypes.join(","));

      // Set closedOnly based on chart filter mode
      params.set("closedOnly", chartFilterMode === "closed" ? "true" : "false");

      // Pagination params
      params.set("page", String(tablePage));
      params.set("pageSize", String(tablePageSize));

      const response = await fetch(`/api/dashboard/summary?${params.toString()}`);
      const result = await response.json();

      if (result.success) {
        const { data } = result;
        setMetrics(data.metrics);
        setCashTrend(data.cashTrend || []);
        setCountryData(data.countryData || []);
        setLeadSourceData(data.leadSourceData || []);
        setOutcomeBreakdown(data.outcomeBreakdown || []);
        setProspectSituationData(data.prospectSituationBreakdown || []);
        setFilterOptions(data.filterOptions || {
          closers: [],
          setters: [],
          funnels: [],
          contents: [],
          countries: [],
          sources: [],
        });
        setRecentCalls(data.recentCalls || []);

        // Update pagination state from server response
        if (data.pagination) {
          setTableTotalItems(data.pagination.totalItems);
          setTableTotalPages(data.pagination.totalPages);
        }
      }
    } catch (error) {
      console.error("Failed to fetch acquisition data:", error);
    } finally {
      if (showLoading) {
        if (tableOnly) {
          setTableLoading(false);
        } else {
          setIsLoading(false);
        }
      }
    }
  }, [globalDateRange, formatForApi, dateField, closers, setters, sources, content, country, funnels, leadTypes, chartFilterMode, tablePage, tablePageSize]);

  // Reset to page 1 when filters change
  useEffect(() => {
    isPaginationChangeRef.current = false;
    setTablePage(1);
  }, [globalDateRange, dateField, closers, setters, sources, content, country, funnels, leadTypes, chartFilterMode]);

  // Fetch data when dependencies change
  useEffect(() => {
    const isTableOnly = isPaginationChangeRef.current;
    isPaginationChangeRef.current = false;
    fetchData(true, isTableOnly);
  }, [fetchData]);

  // Fetch acquisition funnel data — same filters as scorecard
  const fetchFunnelData = useCallback(async () => {
    setFunnelLoading(true);
    try {
      const params = new URLSearchParams();
      const { startDate, endDate } = formatForApi();
      if (startDate) params.set("startDate", startDate);
      if (endDate) params.set("endDate", endDate);
      if (dateField) params.set("dateField", dateField);
      if (closers.length > 0) params.set("closer", closers.join(","));
      if (setters.length > 0) params.set("setter", setters.join(","));
      if (sources.length > 0) params.set("source", sources.join(","));
      if (content) params.set("content", content);
      if (country) params.set("country", country);
      if (funnels.length > 0) params.set("funnel", funnels.join(","));
      const res = await fetch(`/api/acquisition/funnel?${params.toString()}`);
      const result = await res.json();
      if (result.success) setFunnelData(result.data);
    } catch (err) {
      console.error("Failed to fetch funnel data:", err);
    } finally {
      setFunnelLoading(false);
    }
  }, [globalDateRange, formatForApi, dateField, closers, setters, sources, content, country, funnels]);

  useEffect(() => {
    fetchFunnelData();
  }, [fetchFunnelData]);

  // Auto-remove invalid filters when available options change
  useEffect(() => {
    // Skip if filterOptions haven't loaded yet
    if (!filterOptions.closers.length && !filterOptions.setters.length && !filterOptions.funnels.length) {
      return;
    }

    const filtersToRemove: string[] = [];

    // Check closer filter
    if (closers.length > 0 && filterOptions.closers.length > 0 && !closers.every(c => filterOptions.closers.includes(c))) {
      filtersToRemove.push('closer');
    }

    // Check setter filter
    if (setters.length > 0 && filterOptions.setters.length > 0 && !setters.every(s => filterOptions.setters.includes(s))) {
      filtersToRemove.push('setter');
    }

    // Check funnel filter (uses value/label format)
    if (funnels.length > 0 && filterOptions.funnels.length > 0) {
      const funnelValues = filterOptions.funnels.map(f => f.value);
      if (!funnels.every(f => funnelValues.includes(f))) {
        filtersToRemove.push('funnel');
      }
    }

    // Check content filter (uses value/label format)
    if (content && filterOptions.contents.length > 0) {
      const contentValues = filterOptions.contents.map(c => c.value);
      if (!contentValues.includes(content)) {
        filtersToRemove.push('content');
      }
    }

    // Check country filter
    if (country && filterOptions.countries.length > 0 && !filterOptions.countries.includes(country)) {
      filtersToRemove.push('country');
    }

    // Check source filter
    if (sources.length > 0 && filterOptions.sources.length > 0) {
      if (!sources.every(s => filterOptions.sources.includes(s))) {
        filtersToRemove.push('source');
      }
    }

    // Remove invalid filters
    if (filtersToRemove.includes('closer')) setClosers([]);
    if (filtersToRemove.includes('setter')) setSetters([]);
    if (filtersToRemove.includes('funnel')) setFunnels([]);
    if (filtersToRemove.includes('content')) setContents([]);
    if (filtersToRemove.includes('country')) setCountries([]);
    if (filtersToRemove.includes('source')) setSources([]);
  }, [filterOptions, closers, setters, funnels, content, country, sources, setClosers, setSetters, setFunnels, setContents, setCountries, setSources]);

  // Get greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    const firstName = session?.user?.name?.split(" ")[0] || "";
    const timeGreeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
    return firstName ? `${timeGreeting}, ${firstName}` : timeGreeting;
  };

  // Handle clear all filters
  const handleClearAll = () => {
    clearAllFilters();
  };

  // Prepare funnel breakdown data (Applicant vs Opt-in)
  const funnelBreakdownData = [
    { name: "Applicants", value: metrics?.totalApplications || 0 },
    { name: "Opt-ins", value: metrics?.totalOptins || 0 },
  ];

  // Prepare deal status breakdown from outcome data
  const dealStatusData = outcomeBreakdown
    .filter((o) => o._id && o._id.trim() !== "")
    .slice(0, 6)
    .map((o) => ({
      name: o._id,
      value: o.count,
    }));

  // Only show skeleton on initial load, not when filtering (keep old data visible)
  if (isLoading && !metrics) {
    return (
      <div className="space-y-6">
        {/* Metric Cards Skeleton */}
        <div className="grid gap-6 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <MetricCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Funnel Stats Skeleton */}
        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Call Results Skeleton */}
        <div className="grid gap-4 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <StatCardSkeleton key={i} index={i + 4} />
          ))}
        </div>

        {/* Average Cash Metrics Skeleton */}
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <StatCardSkeleton key={i} index={i + 8} />
          ))}
        </div>

        {/* Cash Totals Skeleton */}
        <div className="grid gap-4 grid-cols-2">
          {[0, 1].map((i) => (
            <StatCardSkeleton key={i} index={i + 11} />
          ))}
        </div>

        {/* Additional Stats Skeleton */}
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <AdditionalStatCardSkeleton key={i} index={i} />
          ))}
        </div>

        {/* Chart Skeletons */}
        <div className="grid gap-6 md:grid-cols-2">
          {[0, 1].map((i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.1 }}
              className="relative overflow-hidden bg-card rounded-xl border border-border p-6"
            >
              <div className="h-5 w-40 rounded bg-muted animate-pulse mb-4" />
              <div className="h-[300px] rounded-lg bg-muted/30 animate-pulse" />
            </motion.div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="flex items-center justify-between"
      >
        <motion.h1
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
          className="text-2xl font-bold tracking-tight"
        >
          {getGreeting()}
        </motion.h1>
      </motion.div>

      {/* Filter Bar */}
      <FilterBar
        showDateFieldToggle={true}
        dateField={dateField as "timestamp" | "dateCallWasTaken"}
        onDateFieldChange={(v) => setDateField(v)}
        filters={[
          {
            key: "closer",
            label: "Select Closer",
            options: filterOptions.closers.map((c) => ({ value: c, label: c })),
            value: closers,
            onChange: (v) => setClosers(v as string[]),
            multiple: true,
          },
          {
            key: "setter",
            label: "Select Setter",
            options: filterOptions.setters.map((s) => ({ value: s, label: s })),
            value: setters,
            onChange: (v) => setSetters(v as string[]),
            multiple: true,
          },
          {
            key: "source",
            label: "Select Source",
            options: filterOptions.sources.map((s) => ({ value: s, label: s })),
            value: sources,
            onChange: (v) => setSources(v as string[]),
            multiple: true,
          },
          {
            key: "funnel",
            label: "Select Funnel",
            options: filterOptions.funnels,
            value: funnels,
            onChange: (v) => setFunnels(v as string[]),
            multiple: true,
          },
          {
            key: "leadType",
            label: "Lead Type",
            options: [
              { value: "applicant", label: "Applicant" },
              { value: "optin", label: "Opt-in" },
            ],
            value: leadTypes,
            onChange: (v) => setLeadTypes(v as string[]),
            multiple: true,
          },
        ]}
        onClearAll={handleClearAll}
      />

      {/* Acquisition Funnel + Lead Table — side by side */}
      {(() => {
        const funnelSteps: FunnelStepData[] = funnelData
          ? [
              { label: "Opt-ins", count: funnelData.optins.length, leads: funnelData.optins },
              { label: "Applications", count: funnelData.applications.length, leads: funnelData.applications },
              { label: "Booked Calls", count: funnelData.bookedCalls.length, leads: funnelData.bookedCalls },
              { label: "Showed Calls", count: funnelData.showedCalls.length, leads: funnelData.showedCalls },
              { label: "Closed Calls", count: funnelData.closedCalls.length, leads: funnelData.closedCalls },
            ]
          : [];
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15, ease: [0.25, 0.46, 0.45, 0.94] }}
            className="bg-card rounded-xl border border-border overflow-hidden"
          >
            <div className="px-5 pt-5 pb-3 flex items-center justify-between border-b border-border/50">
              <div>
                <h3 className="text-base font-semibold">Acquisition Funnel</h3>
                <p className="text-xs text-muted-foreground mt-0.5">Click a step to filter the lead list</p>
              </div>
            </div>

            {funnelLoading ? (
              <div className="h-[440px] m-5 rounded-lg bg-muted/30 animate-pulse" />
            ) : funnelData ? (
              <div className="flex p-5 gap-5" style={{ height: "540px" }}>
                {/* Funnel — left column, vertically centered */}
                <div className="flex-shrink-0 w-[58%] flex flex-col justify-center">
                  <FunnelChart
                    steps={funnelSteps}
                    selectedIndex={selectedFunnelIndex}
                    onStepClick={setSelectedFunnelIndex}
                  />
                </div>
                {/* Divider */}
                <div className="w-px bg-border flex-shrink-0" />
                {/* Table — right column, fills height */}
                <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                  <FunnelLeadTable step={funnelSteps[selectedFunnelIndex] ?? null} />
                </div>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-16">Failed to load funnel data</p>
            )}
          </motion.div>
        );
      })()}

      {/* Top Metric Cards - Same as Dashboard */}
      <div className="grid gap-6 md:grid-cols-3">
        <MetricCard
          label="Cash Collected"
          value={formatCurrency(metrics?.totalCashCollected || 0)}
          icon={<DollarIcon />}
          color="brand"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={0}
        />
        <MetricCard
          label="Revenue"
          value={formatCurrency(metrics?.totalRevenue || 0)}
          icon={<TrendingUpIcon />}
          color="green"
          data={cashTrend.map((t) => t.value)}
          tooltipFormatter={formatCurrency}
          index={1}
        />
        <MetricCard
          label="Close Rate"
          value={formatPercent(metrics?.closeRate || 0)}
          icon={<ChartIcon />}
          color="blue"
          data={[]}
          tooltipFormatter={(v) => formatPercent(v)}
          index={2}
        />
      </div>

      {/* Funnel Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Total Applications" value={(metrics?.totalApplications || 0).toLocaleString()} index={0} />
        <StatCard label="Total Opt-ins" value={(metrics?.totalOptins || 0).toLocaleString()} index={1} />
        <StatCard label="Calls Due" value={(metrics?.callsDue || 0).toLocaleString()} index={2} />
        <StatCard label="Calls Taken" value={(metrics?.callsTaken || 0).toLocaleString()} index={3} />
      </div>

      {/* Call Results */}
      <div className="grid gap-4 md:grid-cols-4">
        <StatCard label="Calls Closed" value={(metrics?.callsClosed || 0).toLocaleString()} index={0} />
        <StatCard label="Show Rate" value={formatPercent(metrics?.showRate || 0)} index={1} />
        <StatCard label="Close Rate" value={formatPercent(metrics?.closeRate || 0)} index={2} />
        <StatCard label="Avg. Cash / Call" value={formatCurrency(metrics?.avgCashPerCall || 0)} index={3} />
      </div>

      {/* Average Cash Metrics */}
      <div className="grid gap-4 md:grid-cols-3">
        <StatCard label="Avg. Cash / Application" value={formatCurrency(metrics?.avgCashPerApplication || 0)} index={0} />
        <StatCard label="Avg. Cash / Opt-in" value={formatCurrency(metrics?.avgCashPerOptin || 0)} index={1} />
        <StatCard label="Avg. Cash / Close" value={formatCurrency(metrics?.avgCashPerClose || 0)} index={2} />
      </div>

      {/* Cash Totals */}
      <div className="grid gap-4 grid-cols-2">
        <StatCard label="Total Cash (Applications)" value={formatCurrency(metrics?.totalApplicationsCash || 0)} index={0} />
        <StatCard label="Total Cash (Opt-ins)" value={formatCurrency(metrics?.totalOptinsCash || 0)} index={1} />
      </div>

      {/* Additional Statistics */}
      <div className="grid gap-4 md:grid-cols-3">
        {[
          { label: "New Cash Collected", value: formatCurrency(metrics?.newCashCollected || 0), description: "Total cash from new deals (excludes MRR)" },
          { label: "MRR", value: formatCurrency(metrics?.mrrCash || 0), description: "Monthly recurring revenue" },
          { label: "Total Gross Revenue", value: formatCurrency(metrics?.totalGrossRevenue || 0), description: "Total contract value (all revenue)" },
        ].map((stat, i) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 15, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              duration: 0.4,
              delay: 0.5 + i * 0.06,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
            whileHover={{
              y: -3,
              boxShadow: "0 12px 40px -10px rgba(0, 0, 0, 0.2)",
              transition: { duration: 0.2 },
            }}
            className="bg-card rounded-xl border-2 border-border p-6 transition-colors duration-300 hover:border-border/80"
          >
            <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              {stat.label}
            </p>
            <p className="text-2xl font-bold">{stat.value}</p>
            {stat.description && (
              <motion.p
                className="text-xs text-muted-foreground mt-1"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.7 + i * 0.06 }}
              >
                {stat.description}
              </motion.p>
            )}
          </motion.div>
        ))}
      </div>

      {/* Closes by Country - Full Width */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.55, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border p-6 overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            {chartFilterMode === "closed" ? "Closes" : "Leads"} by Country
          </h3>
          <motion.select
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            value={chartFilterMode}
            onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
          >
            <option value="closed">Closed Leads</option>
            <option value="all">All Leads</option>
          </motion.select>
        </div>
        <div className="h-[400px]">
          <ResponsivePie
            data={toNivoPieData(countryData, chartColors)}
            margin={{ top: 40, right: 120, bottom: 40, left: 120 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={2}
            borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => String(d.label)}
            arcLinkLabelsStraightLength={20}
            arcLinkLabelsDiagonalLength={20}
            enableArcLabels={true}
            arcLabelsSkipAngle={10}
            arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
            arcLabelsTextColor="#ffffff"
            theme={getNivoPieTheme()}
            tooltip={PieChartTooltip}
          />
        </div>
      </motion.div>

      {/* Charts Row 1: Lead Source + Funnel Breakdown */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Lead Source Breakdown */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">
              {chartFilterMode === "closed" ? "Closes" : "Leads"} by Source
            </h3>
            <motion.select
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              value={chartFilterMode}
              onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
              className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
            >
              <option value="closed">Closed Leads</option>
              <option value="all">All Leads</option>
            </motion.select>
          </div>
          <div className="h-[300px]">
            <ResponsivePie
              data={toNivoPieData(leadSourceData, chartColors)}
              margin={{ top: 30, right: 100, bottom: 30, left: 100 }}
              innerRadius={0.5}
              padAngle={1}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={2}
              borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => String(d.label)}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
              arcLabelsTextColor="#ffffff"
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
            />
          </div>
        </motion.div>

        {/* Funnel Breakdown (Applicant vs Opt-in) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.7, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="bg-card rounded-xl border border-border p-6 overflow-hidden"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold">Funnel Breakdown</h3>
            <motion.select
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              value={chartFilterMode}
              onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
              className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
            >
              <option value="closed">Closed Leads</option>
              <option value="all">All Leads</option>
            </motion.select>
          </div>
          <div className="h-[300px]">
            <ResponsivePie
              data={toNivoPieData(funnelBreakdownData, chartColors)}
              margin={{ top: 30, right: 100, bottom: 30, left: 100 }}
              innerRadius={0.5}
              padAngle={1}
              cornerRadius={3}
              activeOuterRadiusOffset={8}
              sortByValue={false}
              colors={{ datum: "data.color" }}
              borderWidth={2}
              borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
              enableArcLinkLabels={true}
              arcLinkLabelsSkipAngle={10}
              arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
              arcLinkLabelsThickness={2}
              arcLinkLabelsColor={{ from: "color" }}
              arcLinkLabel={(d) => String(d.label)}
              enableArcLabels={true}
              arcLabelsSkipAngle={10}
              arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
              arcLabelsTextColor="#ffffff"
              theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
            />
          </div>
        </motion.div>
      </div>

      {/* Deal Status Breakdown - Full Width */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border p-6 overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Deal Status Breakdown</h3>
          <motion.select
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            value={chartFilterMode}
            onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
          >
            <option value="closed">Closed Leads</option>
            <option value="all">All Leads</option>
          </motion.select>
        </div>
        <div className="h-[400px]">
          <ResponsivePie
            data={toNivoPieData(dealStatusData, chartColors)}
            margin={{ top: 40, right: 140, bottom: 40, left: 140 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={2}
            borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => String(d.label)}
            enableArcLabels={true}
            arcLabelsSkipAngle={10}
            arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
            arcLabelsTextColor="#ffffff"
            theme={getNivoPieTheme()}
              tooltip={PieChartTooltip}
          />
        </div>
      </motion.div>

      {/* Prospect Situation Breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.85, ease: [0.25, 0.46, 0.45, 0.94] }}
        className="bg-card rounded-xl border border-border p-6 overflow-hidden"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Prospect Situation</h3>
          <motion.select
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            value={chartFilterMode}
            onChange={(e) => setChartFilterMode(e.target.value as "closed" | "all")}
            className="h-9 px-3 rounded-md border border-input bg-background text-sm min-w-[140px] cursor-pointer transition-colors hover:border-foreground/30"
          >
            <option value="closed">Closed Leads</option>
            <option value="all">All Leads</option>
          </motion.select>
        </div>
        <div className="h-[400px]">
          <ResponsivePie
            data={toNivoPieData(prospectSituationData, chartColors)}
            margin={{ top: 40, right: 200, bottom: 40, left: 200 }}
            innerRadius={0.5}
            padAngle={1}
            cornerRadius={3}
            activeOuterRadiusOffset={8}
            sortByValue={false}
            colors={{ datum: "data.color" }}
            borderWidth={2}
            borderColor={{ from: "color", modifiers: [["darker", 0.6]] }}
            enableArcLinkLabels={true}
            arcLinkLabelsSkipAngle={10}
            arcLinkLabelsTextColor={{ from: "color", modifiers: [["brighter", 1]] }}
            arcLinkLabelsThickness={2}
            arcLinkLabelsColor={{ from: "color" }}
            arcLinkLabel={(d) => String(d.label)}
            arcLinkLabelsStraightLength={24}
            arcLinkLabelsDiagonalLength={24}
            enableArcLabels={true}
            arcLabelsSkipAngle={10}
            arcLabel={(d) => `${d.data.percentage.toFixed(1)}%`}
            arcLabelsTextColor="#ffffff"
            theme={getNivoPieTheme()}
            tooltip={PieChartTooltip}
          />
        </div>
      </motion.div>

      {/* Recent Calls Table */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.9, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <div className="mb-4">
          <h3 className="text-lg font-semibold">Recent Calls</h3>
          <p className="text-sm text-muted-foreground">Latest call activity</p>
        </div>
        <DataTable
          data={recentCalls}
          columns={recentCallsColumns}
          rowKey="id"
          emptyMessage="No recent calls found."
          loading={tableLoading}
          serverPagination={{
            currentPage: tablePage,
            pageSize: tablePageSize,
            totalItems: tableTotalItems,
            totalPages: tableTotalPages,
            pageSizeOptions: [10, 25, 50, 100],
            onPageChange: handlePageChange,
            onPageSizeChange: handlePageSizeChange,
          }}
          onEdit={handleEditCall}
          onDelete={handleDeleteCall}
        />
      </motion.div>

      {/* Edit Dialog */}
      <EditRowDialog
        open={!!editingCall}
        onOpenChange={(open) => !open && setEditingCall(null)}
        title="Edit Call"
        description="Update call information"
        fields={CALL_EDIT_FIELDS}
        data={editingCall as Record<string, unknown> | null}
        onSave={handleSaveCall}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={!!deletingCall}
        onOpenChange={(open) => !open && setDeletingCall(null)}
        title="Delete Call"
        itemName={deletingCall?.leadName}
        onConfirm={confirmDeleteCall}
      />
    </div>
  );
}
